import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { setCurrentUser } from '../../ducks/user.duck';

// ================ Async Thunks ================ //

//////////////////
// Upload Image //
//////////////////
export const uploadImageThunk = createAsyncThunk(
  'ProfileSettingsPage/uploadImage',
  ({ id, file }, { rejectWithValue, extra: sdk }) => {
    const bodyParams = {
      image: file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        return { id, uploadedImage };
      })
      .catch(e => {
        return rejectWithValue({ id, error: storableError(e) });
      });
  }
);
// Backward compatible wrapper for the uploadImage thunk.
// On a successful upload we immediately persist the new profile image through
// the EXISTING updateProfile chain (→ setCurrentUser → Topbar avatar refresh),
// so the photo survives a page refresh without a separate "Save changes" click.
// Only profileImageId is written here — an in-progress, unsaved bio/name edit in
// the form is never swept into this image write.
export const uploadImage = actionPayload => async dispatch => {
  const result = await dispatch(uploadImageThunk(actionPayload));
  if (uploadImageThunk.fulfilled.match(result)) {
    const imageId = result.payload?.uploadedImage?.id;
    if (imageId) {
      return dispatch(updateProfileThunk({ profileImageId: imageId }));
    }
  }
  return result;
};

////////////////////
// Update Profile //
////////////////////
export const updateProfileThunk = createAsyncThunk(
  'ProfileSettingsPage/updateProfile',
  (actionPayload, { dispatch, rejectWithValue, extra: sdk }) => {
    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(setCurrentUser(currentUser));
        return response;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrapper for the updateProfile thunk
export const updateProfile = actionPayload => dispatch => {
  return dispatch(updateProfileThunk(actionPayload));
};

// ================ Slice ================ //

// The manual "Save changes" submit always sends firstName (a required field),
// while the image auto-save sends only { profileImageId }. Absence of firstName
// therefore reliably identifies the image-only write.
const isImageOnlyUpdate = action => !('firstName' in (action.meta?.arg || {}));

const profileSettingsPageSlice = createSlice({
  name: 'ProfileSettingsPage',
  initialState: {
    image: null,
    uploadImageError: null,
    uploadInProgress: false,
    updateInProgress: false,
    updateProfileError: null,
    // Persist error for the auto-saved profile image, kept separate from
    // updateProfileError so it surfaces in the avatar area (not by Save changes).
    imageUpdateError: null,
  },
  reducers: {
    clearUpdatedForm: state => {
      state.updateProfileError = null;
      state.uploadImageError = null;
      state.imageUpdateError = null;
    },
  },
  extraReducers: builder => {
    builder
      // uploadImage cases
      .addCase(uploadImageThunk.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.image = { id, file };
        state.uploadInProgress = true;
        state.uploadImageError = null;
        state.imageUpdateError = null;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action) => {
        const { id, uploadedImage } = action.payload;
        const { file } = state.image || {};
        state.image = { id, imageId: uploadedImage.id, file, uploadedImage };
        state.uploadInProgress = false;
      })
      .addCase(uploadImageThunk.rejected, (state, action) => {
        state.image = null;
        state.uploadInProgress = false;
        state.uploadImageError = action.payload.error;
      })
      // updateProfile cases.
      // The image-only auto-save (dispatched by the uploadImage chain) writes
      // just { profileImageId } — no firstName — so it routes its in-flight/error
      // state to the avatar area instead of the Save-changes submit error.
      .addCase(updateProfileThunk.pending, (state, action) => {
        state.updateInProgress = true;
        if (isImageOnlyUpdate(action)) {
          state.imageUpdateError = null;
        } else {
          state.updateProfileError = null;
        }
      })
      .addCase(updateProfileThunk.fulfilled, state => {
        state.image = null;
        state.updateInProgress = false;
      })
      .addCase(updateProfileThunk.rejected, (state, action) => {
        state.image = null;
        state.updateInProgress = false;
        if (isImageOnlyUpdate(action)) {
          state.imageUpdateError = action.payload;
        } else {
          state.updateProfileError = action.payload;
        }
      });
  },
});

export const { clearUpdatedForm } = profileSettingsPageSlice.actions;
export default profileSettingsPageSlice.reducer;
